import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { ContactPoint } from '@medplum/fhirtypes';

export function isAValidPhoneNumber(number: string): boolean {
  return /^[\d+() -]+$/.test(number);
}

export function formatContactPointValue(contactPoint: ContactPoint): string {
  if (!['phone', 'fax', 'sms', 'pager'].includes(contactPoint.system ?? '')) {
    return contactPoint.value ?? '';
  }
  return cleanPhoneNumberForEditing(contactPoint.value || '');
}

/**
 * Cleans a phone number for editing by removing formatting but preserving the leading +
 * This is used for input fields where users can edit phone numbers
 */
export function cleanPhoneNumberForEditing(phoneNumber: string): string {
  return phoneNumber.replace(/(?!^\+)[^\d]/g, '');
}

export function normalizePhoneNumber(phoneNumber: string): string {
  // Remove all non-digit characters except the leading +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '').trim();

  // If it starts with +, return as is
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If it starts with 1 and has 11 digits, add + prefix
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+${cleaned}`;
  }

  // If it has 10 digits, assume US number and add +1 prefix
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // For other cases, add + prefix if not present
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
}

export function formatPhoneNumber(input?: string): string {
  if (!input) return '';

  // Try to parse with libphonenumber-js first for international numbers
  try {
    const parsed = parsePhoneNumberWithError(input);

    // For other non-US international numbers, use the same formatting pattern
    if (parsed.country !== 'US') {
      const nationalNumber = parsed.nationalNumber;
      const countryCode = parsed.countryCallingCode;

      if (nationalNumber.length >= 10) {
        const lastTenDigits = nationalNumber.slice(-10);
        const area = lastTenDigits.slice(0, 3);
        const prefix = lastTenDigits.slice(3, 6);
        const line = lastTenDigits.slice(6);

        return `+${countryCode} (${area}) ${prefix}-${line}`;
      }

      // Fallback for shorter numbers
      return `+${countryCode} ${nationalNumber}`;
    }
  } catch {
    // Continue to existing logic if parsing fails
  }

  // Use the existing logic for US numbers and fallback cases
  const cleaned = input.replace(/\D/g, '');
  const isInternational = input.startsWith('+');

  if (cleaned.length < 10) return input;

  const localPart = cleaned.slice(-10);
  const countryCode = cleaned.slice(0, cleaned.length - 10);

  const area = localPart.slice(0, 3);
  const prefix = localPart.slice(3, 6);
  const line = localPart.slice(6);

  // US number: country code is empty or '1' or '+1'
  if (
    (!isInternational && (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1')))) ||
    (isInternational && countryCode === '1')
  ) {
    return `(${area}) ${prefix}-${line}`;
  }

  if (isInternational && countryCode) {
    return `+${countryCode} (${area}) ${prefix}-${line}`;
  }

  return `(${area}) ${prefix}-${line}`;
}

export function isSamePhoneNumber(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  const normalizedA = normalizePhoneNumber(a).replace('+', '');
  const normalizedB = normalizePhoneNumber(b).replace('+', '');
  return normalizedA === normalizedB;
}
